import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { DropzoneState, useDropzone } from "react-dropzone";
import { emailRegex } from "../theme/configurationVariables";
import { format } from "date-fns";
import { useRequest } from "./RequestContext";
import { useNavigate } from "react-router";

export interface QuestionContextProps {
    questionForm: PerfilamientoRequestModel;
    dropzoneInput: DropzoneState;
    fileUploadList: File[];
    questionErrorForm: PerfilamientoRequestErrorModel;
    questionCreated: string;
    handleChangeQuestionForm: (e: any) => void;
    handleChangeQuestionFormDate: (name: string) => (date: Date | null) => void;
    handleChangeQuestionFormCheck: (e: any) => void;
    handleChangeQuestionFormAnyField: (name: string, value: any) => void;
    handleClickDeleteFile: (file: File) => void;
    handleClickSubmitQuestion: () => Promise<void>;
}

export const QuestionContext = createContext<QuestionContextProps | null>(null);

export const QuestionProvider = ({ children }: PropsWithChildren) => {
    const { sendRequest } = useRequest();
    const navigate = useNavigate();

    const [questionForm, setQuestionForm] = useState<PerfilamientoRequestModel>(getPerfilamientoRequestModel());
    const [questionErrorForm, setQuestionErrorForm] = useState<PerfilamientoRequestErrorModel>(getPerfilamientoRequestErrorModel());
    const [fileUploadList, setFileUploadList] = useState<File[]>([]);
    const [questionCreated, setQuestionCreated] = useState("");

    const handleChangeQuestionForm = (e: any) => {
        setQuestionForm({ ...questionForm, [e.target.name]: e.target.value });
        setQuestionErrorForm({ ...questionErrorForm, [e.target.name]: false });
    };
    const handleChangeQuestionFormDate = (name: string) => (date: Date | null) => setQuestionForm({ ...questionForm, [name]: date });
    const handleChangeQuestionFormCheck = (e: any) => {
        setQuestionForm({ ...questionForm, [e.target.name]: e.target.checked });
        setQuestionErrorForm({ ...questionErrorForm, [e.target.name]: false });
    };
    const handleChangeQuestionFormAnyField = (name: string, value: any) => {
        setQuestionForm({ ...questionForm, [name]: value });
        setQuestionErrorForm({ ...questionErrorForm, [name]: false });
    };

    const dropzoneInput = useDropzone({
        maxFiles: 3,
        maxSize: 10485760,
        multiple: true,
        onDropAccepted: (files: File[]) => setFileUploadList([...fileUploadList, ...files]),
    });

    const handleClickDeleteFile = (file: File) => {
        setFileUploadList(fileUploadList.filter((x) => file !== x));
    };

    const handleClickSubmitQuestion = async () => {
        const { pregunta, correo, idEstado, aceptaTerminosCondiciones } = questionForm;

        const errors: PerfilamientoRequestErrorModel = {
            correo: !correo || !correo.match(emailRegex),
            idEstado: !idEstado,
            pregunta: !pregunta,
            aceptaTerminosCondiciones: !aceptaTerminosCondiciones,
        };

        setQuestionErrorForm(errors);

        if (errors.correo || errors.idEstado || errors.pregunta) {
            window.scrollTo({
                left: 0,
                top: 0,
                behavior: "smooth",
            });
        }

        if (Object.values(errors).some((x) => x)) return;

        const {
            alergiaLatex,
            alergiasAlimentos,
            alergiasAntibioticos,
            alergiasMedicamentos,
            enfermedades,
            fechaNacimiento,
            idMedioContacto,
            idSexo,
            medicamentoActual,
            otraAlergiaAlimento,
            otraAlergiaAntibiotico,
            otraAlergiaMedicamento,
            otraEnfermedad,
        } = questionForm;

        const request = {
            correo,
            pregunta,
            idEstado,
            fechaNacimiento: !fechaNacimiento ? null : format(fechaNacimiento, "yyyy-MM-dd"),
            idSexo: typeof idSexo === "string" ? parseInt(idSexo) : idSexo,
            otraEnfermedad: !otraEnfermedad ? null : otraEnfermedad,
            otraAlergiaAlimento: !otraAlergiaAlimento ? null : otraAlergiaAlimento,
            otraAlergiaAntibiotico: !otraAlergiaAntibiotico ? null : otraAlergiaAntibiotico,
            otraAlergiaMedicamento: !otraAlergiaMedicamento ? null : otraAlergiaMedicamento,
            alergiaLatex,
            medicamentoActual: !medicamentoActual ? null : medicamentoActual,
            idMedioContacto: typeof idMedioContacto === "string" ? parseInt(idMedioContacto) : idMedioContacto,
            alergiasAlimentos,
            alergiasAntibioticos,
            alergiasMedicamentos,
            enfermedades,
            medicamentosActuales: [],
        };

        const response = await sendRequest<string>({
            path: "/api/perfilamiento",
            body: request,
            loaderText: "Enviando pregunta...",
            method: "post",
        });

        if (response.hasError) return;

        if (fileUploadList.length > 0) {
            const form = new FormData();
            fileUploadList.forEach((file, index) => {
                form.append(`file-${index + 1}`, file);
            });

            await sendRequest<boolean>({
                path: `/api/perfilamiento/${response.data}/archivos`,
                formData: form,
                loaderText: "Cargando archivos y documentos...",
                method: "post",
            });
        }

        setQuestionCreated(response.data);
        setQuestionErrorForm(getPerfilamientoRequestErrorModel());
        setQuestionForm(getPerfilamientoRequestModel());

        navigate("/preguntra-recibida");
    };

    return (
        <QuestionContext.Provider
            value={{
                questionForm,
                dropzoneInput,
                fileUploadList,
                questionErrorForm,
                questionCreated,
                handleClickDeleteFile,
                handleChangeQuestionForm,
                handleChangeQuestionFormDate,
                handleChangeQuestionFormCheck,
                handleChangeQuestionFormAnyField,
                handleClickSubmitQuestion,
            }}
        >
            {children}
        </QuestionContext.Provider>
    );
};

export const useQuestion = () => useContext(QuestionContext) as QuestionContextProps;

export interface PerfilamientoRequestModel {
    correo: string;
    pregunta: string;
    idEstado: string;
    fechaNacimiento: null | Date;
    idSexo: number | string;
    otraEnfermedad: string;
    otraAlergiaAlimento: string;
    otraAlergiaAntibiotico: string;
    otraAlergiaMedicamento: string;
    alergiaLatex: boolean;
    medicamentoActual: string;
    idMedioContacto: number | string;
    alergiasAlimentos: string[];
    alergiasAntibioticos: string[];
    alergiasMedicamentos: string[];
    enfermedades: string[];
    medicamentosActuales: string[];
    aceptaTerminosCondiciones: boolean;
}

export interface PerfilamientoRequestErrorModel {
    correo: boolean;
    pregunta: boolean;
    idEstado: boolean;
    aceptaTerminosCondiciones: boolean;
}

export const getPerfilamientoRequestModel = (): PerfilamientoRequestModel => ({
    alergiaLatex: false,
    alergiasAlimentos: [],
    alergiasAntibioticos: [],
    alergiasMedicamentos: [],
    correo: "",
    enfermedades: [],
    fechaNacimiento: null,
    idEstado: "",
    idMedioContacto: "2",
    idSexo: "1",
    medicamentoActual: "",
    medicamentosActuales: [],
    otraAlergiaAlimento: "",
    otraAlergiaAntibiotico: "",
    otraAlergiaMedicamento: "",
    otraEnfermedad: "",
    pregunta: "",
    aceptaTerminosCondiciones: false,
});

export const getPerfilamientoRequestErrorModel = (): PerfilamientoRequestErrorModel => ({
    correo: false,
    idEstado: false,
    pregunta: false,
    aceptaTerminosCondiciones: false,
});

export const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "5px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
};

export const focusedStyle = {
    borderColor: "#2196f3",
};

export const acceptStyle = {
    borderColor: "#257590",
};

export const rejectStyle = {
    borderColor: "#ff1744",
};
